import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';

import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import {
  snapshot,
  type SnapshotGoal,
  type SnapshotGoalInput,
  type SnapshotGoalType,
  type SnapshotTrackingConfig,
} from 'services/api';

import { readSnapshotApiError, SnapshotEditorShell } from './SnapshotEditorShell';

type GoalPlanEditorProps = {
  open: boolean;
  goal: SnapshotGoal | null;
  sourceQuestionKey?: string;
  initialInput?: SnapshotGoalInput | null;
  userTimezone?: string;
  onOpenChange: (open: boolean) => void;
  onSaved: (goal: SnapshotGoal) => Promise<void> | void;
};

type StepDraft = { key?: string; label: string };

type GoalDraft = {
  title: string;
  motivation: string;
  nextAction: string;
  goalType: SnapshotGoalType;
  startDate: string;
  timezone: string;
  unit: string;
  completionLabel: string;
  target: string;
  periodUnit: 'day' | 'week' | 'month';
  requiredPeriods: string;
  consecutive: boolean;
  weekStartsOn: string;
  targetDate: string;
  steps: StepDraft[];
  prompt: string;
  reflectiveHasTarget: boolean;
};

const localIsoDate = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const defaultCompletionLabel = (unit: string) => {
  const normalized = unit.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!normalized || normalized === 'times') return 'completion';
  const singularize = (word: string) => {
    if (word.endsWith('ies') && word.length > 3) return `${word.slice(0, -3)}y`;
    if (/(sses|shes|ches|xes|zes)$/.test(word)) return word.slice(0, -2);
    if (word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1);
    return word;
  };
  if (normalized.includes(' of ')) {
    const [head, ...remainder] = normalized.split(' of ');
    const words = head.split(' ');
    words[words.length - 1] = singularize(words[words.length - 1]);
    return `${words.join(' ')} of ${remainder.join(' of ')}`;
  }
  const words = normalized.split(' ');
  words[words.length - 1] = singularize(words[words.length - 1]);
  return words.join(' ');
};

const blankDraft = (timezone: string): GoalDraft => ({
  title: '',
  motivation: '',
  nextAction: '',
  goalType: 'recurring',
  startDate: localIsoDate(),
  timezone: timezone || 'UTC',
  unit: 'times',
  completionLabel: 'completion',
  target: '1',
  periodUnit: 'week',
  requiredPeriods: '8',
  consecutive: true,
  weekStartsOn: '0',
  targetDate: localIsoDate(),
  steps: [{ label: '' }],
  prompt: '',
  reflectiveHasTarget: false,
});

const draftForGoal = (goal: SnapshotGoal, timezone: string): GoalDraft => {
  const draft = blankDraft(goal.timezone || timezone);
  draft.title = goal.title;
  draft.motivation = goal.motivation;
  draft.nextAction = goal.next_action || '';
  draft.goalType = goal.goal_type ?? 'recurring';
  draft.startDate = goal.start_date ?? localIsoDate();
  draft.timezone = goal.timezone || timezone || 'UTC';
  const config = goal.tracking_config;
  if (goal.goal_type === 'recurring' && 'target_per_period' in config) {
    draft.periodUnit = config.period_unit;
    draft.target = String(config.target_per_period);
    draft.requiredPeriods = String(config.required_periods);
    draft.consecutive = config.consecutive;
    draft.weekStartsOn = String(config.week_starts_on);
    draft.unit = config.unit;
    draft.completionLabel = config.completion_label || defaultCompletionLabel(config.unit);
  } else if (goal.goal_type === 'count' && 'target_count' in config) {
    draft.target = String(config.target_count);
    draft.unit = config.unit;
    draft.completionLabel = config.completion_label || defaultCompletionLabel(config.unit);
  } else if (goal.goal_type === 'quantity' && 'target_value' in config) {
    draft.target = String(config.target_value);
    draft.unit = config.unit;
  } else if (goal.goal_type === 'milestone' && 'milestones' in config) {
    draft.steps = config.milestones.map((step) => ({ key: step.key, label: step.label }));
  } else if (goal.goal_type === 'deadline' && 'steps' in config) {
    draft.targetDate = config.target_date;
    draft.steps = config.steps.map((step) => ({ key: step.key, label: step.label }));
  } else if (goal.goal_type === 'reflective' && 'prompt' in config) {
    draft.prompt = config.prompt;
    draft.reflectiveHasTarget = typeof config.target_reflections === 'number';
    if (draft.reflectiveHasTarget) draft.target = String(config.target_reflections);
  }
  return draft;
};

const draftForInput = (input: SnapshotGoalInput, timezone: string): GoalDraft => {
  const draft = blankDraft(input.timezone || timezone);
  draft.title = input.title;
  draft.motivation = input.motivation;
  draft.nextAction = input.next_action || '';
  draft.goalType = input.goal_type;
  draft.startDate = input.start_date;
  draft.timezone = input.timezone;
  const config = input.tracking_config;
  if (input.goal_type === 'recurring' && 'target_per_period' in config) {
    draft.periodUnit = config.period_unit; draft.target = String(config.target_per_period); draft.requiredPeriods = String(config.required_periods); draft.consecutive = config.consecutive; draft.weekStartsOn = String(config.week_starts_on); draft.unit = config.unit; draft.completionLabel = config.completion_label || defaultCompletionLabel(config.unit);
  } else if (input.goal_type === 'count' && 'target_count' in config) { draft.target = String(config.target_count); draft.unit = config.unit; draft.completionLabel = config.completion_label || defaultCompletionLabel(config.unit); }
  else if (input.goal_type === 'quantity' && 'target_value' in config) { draft.target = String(config.target_value); draft.unit = config.unit; }
  else if (input.goal_type === 'milestone' && 'milestones' in config) { draft.steps = config.milestones; }
  else if (input.goal_type === 'deadline' && 'steps' in config) { draft.targetDate = config.target_date; draft.steps = config.steps; }
  else if (input.goal_type === 'reflective' && 'prompt' in config) {
    draft.prompt = config.prompt;
    draft.reflectiveHasTarget = typeof config.target_reflections === 'number';
    if (draft.reflectiveHasTarget) draft.target = String(config.target_reflections);
  }
  return draft;
};

const goalTypeLabels: Record<SnapshotGoalType, string> = {
  recurring: 'Recurring rhythm',
  count: 'Count toward a total',
  quantity: 'Measure a quantity',
  milestone: 'Complete milestones',
  deadline: 'Finish steps by a date',
  reflective: 'Reflect regularly',
};

const selectClass = 'mt-2 min-h-11 w-full rounded-xl border border-input bg-white px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-foundation-base-darkbase-dark-500';

const parseIsoDate = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const parsed = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatPlanDate = (value: string) => {
  const parsed = parseIsoDate(value);
  return parsed
    ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(parsed)
    : value;
};

const isoWeekday = (value: Date) => (value.getDay() + 6) % 7;

const nextWeekBoundary = (value: Date, weekStartsOn: number) => {
  const next = new Date(value);
  const offset = (weekStartsOn - isoWeekday(value) + 7) % 7;
  next.setDate(next.getDate() + (offset || 7));
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`;
};

export const GoalPlanEditor = ({
  open,
  goal,
  sourceQuestionKey,
  initialInput,
  userTimezone = 'UTC',
  onOpenChange,
  onSaved,
}: GoalPlanEditorProps) => {
  const initial = useMemo(
    () => goal ? draftForGoal(goal, userTimezone) : initialInput ? draftForInput(initialInput, userTimezone) : blankDraft(userTimezone),
    [goal, initialInput, userTimezone],
  );
  const [draft, setDraft] = useState<GoalDraft>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [restartWarning, setRestartWarning] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(initial);
    setSaving(false);
    setError('');
    setFieldErrors({});
    setRestartWarning(false);
  }, [initial, open]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const typeChanged = Boolean(goal?.goal_type && goal.goal_type !== draft.goalType);
  const trackingSummary = useMemo(() => {
    if (draft.goalType === 'reflective') {
      return {
        primary: draft.reflectiveHasTarget
          ? `${draft.target || '0'} reflections to finish`
          : 'Ongoing reflection practice',
        secondary: [
          `Start tracking ${formatPlanDate(draft.startDate)}`,
          'Daily check-ins begin the next day',
        ].join(' · '),
      };
    }
    if (draft.goalType !== 'recurring') return null;
    const period = draft.periodUnit;
    const periodPlural = `${period}s`;
    const weekRange = draft.weekStartsOn === '6' ? 'Sunday–Saturday' : 'Monday–Sunday';
    const start = parseIsoDate(draft.startDate);
    const weekStartsOn = Number(draft.weekStartsOn);
    const firstFullWeek = period === 'week' && start && [0, 6].includes(weekStartsOn)
      && isoWeekday(start) !== weekStartsOn
      ? nextWeekBoundary(start, weekStartsOn)
      : null;
    return {
      primary: `${draft.target || '0'} ${draft.unit.trim() || 'times'} per ${period} · ${draft.requiredPeriods || '0'} ${draft.consecutive ? 'consecutive ' : ''}${periodPlural}`,
      secondary: [
        `Quick action: Mark 1 ${draft.completionLabel.trim() || 'completion'} complete`,
        ...(period === 'week' ? [`Weeks run ${weekRange}`] : []),
        `Start tracking ${formatPlanDate(draft.startDate)}`,
        ...(firstFullWeek ? [`First full week begins ${formatPlanDate(firstFullWeek)}`] : []),
        'Daily check-ins begin the next day',
      ].join(' · '),
    };
  }, [draft]);
  const partialWeekWarning = useMemo(() => {
    if (draft.goalType !== 'recurring' || draft.periodUnit !== 'week') return null;
    const start = parseIsoDate(draft.startDate);
    const weekStartsOn = Number(draft.weekStartsOn);
    if (!start || ![0, 6].includes(weekStartsOn) || isoWeekday(start) === weekStartsOn) return null;
    return `You can track immediately. This partial week is a getting-started period; your full measured weeks begin ${formatPlanDate(nextWeekBoundary(start, weekStartsOn))}.`;
  }, [draft.goalType, draft.periodUnit, draft.startDate, draft.weekStartsOn]);

  const updateStep = (index: number, label: string) => {
    setDraft((current) => ({
      ...current,
      steps: current.steps.map((step, stepIndex) => stepIndex === index ? { ...step, label } : step),
    }));
  };

  const moveStep = (index: number, offset: -1 | 1) => {
    const target = index + offset;
    if (target < 0 || target >= draft.steps.length) return;
    setDraft((current) => {
      const steps = [...current.steps];
      [steps[index], steps[target]] = [steps[target], steps[index]];
      return { ...current, steps };
    });
  };

  const updateUnit = (unit: string) => {
    setDraft((current) => {
      const previousDefault = defaultCompletionLabel(current.unit);
      const completionLabel = !current.completionLabel.trim()
        || current.completionLabel.trim().toLowerCase() === previousDefault
        ? defaultCompletionLabel(unit)
        : current.completionLabel;
      return { ...current, unit, completionLabel };
    });
  };

  const trackingConfig = (): SnapshotTrackingConfig => {
    if (draft.goalType === 'recurring') {
      return {
        period_unit: draft.periodUnit,
        target_per_period: Number(draft.target),
        required_periods: Number(draft.requiredPeriods),
        consecutive: draft.consecutive,
        week_starts_on: Number(draft.weekStartsOn),
        unit: draft.unit,
        completion_label: draft.completionLabel,
      };
    }
    if (draft.goalType === 'count') return { target_count: Number(draft.target), unit: draft.unit, completion_label: draft.completionLabel };
    if (draft.goalType === 'quantity') return { target_value: Number(draft.target), unit: draft.unit };
    if (draft.goalType === 'milestone') return { milestones: draft.steps };
    if (draft.goalType === 'deadline') return { target_date: draft.targetDate, steps: draft.steps };
    return {
      prompt: draft.prompt,
      ...(draft.reflectiveHasTarget ? { target_reflections: Number(draft.target) } : {}),
    };
  };

  const save = async (confirmProgressRestart = false) => {
    setSaving(true);
    setError('');
    setFieldErrors({});
    const input: SnapshotGoalInput = {
      ...(sourceQuestionKey ? { source_question_key: sourceQuestionKey } : {}),
      title: draft.title,
      motivation: draft.motivation,
      next_action: draft.nextAction,
      goal_type: draft.goalType,
      tracking_config: trackingConfig(),
      timezone: draft.timezone,
      start_date: draft.startDate,
      check_in_interval_days: 1,
      ...(confirmProgressRestart ? { confirm_progress_restart: true } : {}),
    };
    try {
      const saved = goal
        ? await snapshot.updateGoalPlan(goal.id, input)
        : await snapshot.createGoal(input);
      await onSaved(saved);
      onOpenChange(false);
    } catch (saveError) {
      const parsed = readSnapshotApiError(saveError);
      setError(parsed.message);
      setFieldErrors(parsed.fields);
      if (parsed.status === 409 && parsed.fields.confirm_progress_restart) {
        setRestartWarning(true);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <SnapshotEditorShell
      open={open}
      title={goal ? 'Adjust this goal' : initialInput ? 'Review this tracking suggestion' : 'Set up a seasonal goal'}
      description="Choose a tracking style that matches the action—not the other way around."
      dirty={dirty && !saving}
      onOpenChange={onOpenChange}
      maxWidthClassName="max-w-[720px]"
    >
      <div className="space-y-5">
        <div>
            <Label htmlFor="snapshot-goal-title">Goal title</Label>
            <Input id="snapshot-goal-title" className="mt-2" maxLength={200} value={draft.title} onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))} />
            {fieldErrors.title ? <p className="mt-1 text-xs text-red-700">{fieldErrors.title}</p> : null}
        </div>
        <div>
          <Label htmlFor="snapshot-goal-motivation">Why this matters</Label>
          <Textarea id="snapshot-goal-motivation" className="mt-2 min-h-20" maxLength={4000} value={draft.motivation} onChange={(event) => setDraft((current) => ({ ...current, motivation: event.target.value }))} />
        </div>
        <div>
          <Label htmlFor="snapshot-goal-next-action">Next action <span className="font-normal text-muted-foreground">· optional</span></Label>
          <Input id="snapshot-goal-next-action" className="mt-2" maxLength={1000} value={draft.nextAction} onChange={(event) => setDraft((current) => ({ ...current, nextAction: event.target.value }))} />
        </div>
        <div>
          <Label htmlFor="snapshot-goal-type">Tracking style</Label>
          <select id="snapshot-goal-type" className={selectClass} value={draft.goalType} onChange={(event) => { setDraft((current) => ({ ...current, goalType: event.target.value as SnapshotGoalType })); setRestartWarning(false); }}>
            {(Object.keys(goalTypeLabels) as SnapshotGoalType[]).map((type) => <option key={type} value={type}>{goalTypeLabels[type]}</option>)}
          </select>
          {typeChanged ? <p className="mt-2 rounded-xl bg-foundation-orange-50 px-3 py-2 text-xs leading-5 text-foundation-orange-900">Changing tracking style preserves your history but may start the current progress calculation at zero.</p> : null}
        </div>

        <div className="rounded-2xl bg-foundation-calm-blue-50 p-4 sm:p-5">
          {draft.goalType === 'recurring' ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label htmlFor="snapshot-period-unit">Period</Label><select id="snapshot-period-unit" className={selectClass} value={draft.periodUnit} onChange={(event) => setDraft((current) => ({ ...current, periodUnit: event.target.value as GoalDraft['periodUnit'] }))}><option value="day">Day</option><option value="week">Week</option><option value="month">Month</option></select></div>
              <div><Label htmlFor="snapshot-target-per-period">Target per period</Label><Input id="snapshot-target-per-period" type="number" min="1" step="1" className="mt-2 bg-white" value={draft.target} onChange={(event) => setDraft((current) => ({ ...current, target: event.target.value }))} /></div>
              <div><Label htmlFor="snapshot-required-periods">Number of periods</Label><Input id="snapshot-required-periods" type="number" min="1" step="1" className="mt-2 bg-white" value={draft.requiredPeriods} onChange={(event) => setDraft((current) => ({ ...current, requiredPeriods: event.target.value }))} /></div>
              <div><Label htmlFor="snapshot-recurring-unit">Unit</Label><Input id="snapshot-recurring-unit" className="mt-2 bg-white" value={draft.unit} onChange={(event) => updateUnit(event.target.value)} /></div>
              <div className="sm:col-span-2"><Label htmlFor="snapshot-recurring-completion-label">What one completion is called</Label><Input id="snapshot-recurring-completion-label" className="mt-2 bg-white" maxLength={80} placeholder="workout" value={draft.completionLabel} onChange={(event) => setDraft((current) => ({ ...current, completionLabel: event.target.value }))} /><p className="mt-1 text-xs leading-5 text-muted-foreground">Used for the quick action: “Mark 1 {draft.completionLabel.trim() || 'completion'} complete.”</p>{fieldErrors.completion_label ? <p className="mt-1 text-xs text-red-700">{fieldErrors.completion_label}</p> : null}</div>
              {draft.periodUnit === 'week' ? <div><Label htmlFor="snapshot-week-start">Week starts on</Label><select id="snapshot-week-start" className={selectClass} value={draft.weekStartsOn} onChange={(event) => setDraft((current) => ({ ...current, weekStartsOn: event.target.value }))}><option value="0">Monday</option><option value="6">Sunday</option></select></div> : null}
              <label className="flex min-h-11 items-center gap-3 self-end text-sm"><Checkbox checked={draft.consecutive} onCheckedChange={(checked) => setDraft((current) => ({ ...current, consecutive: checked === true }))} /> Require consecutive periods</label>
            </div>
          ) : null}

          {draft.goalType === 'count' || draft.goalType === 'quantity' ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label htmlFor="snapshot-goal-target">Target</Label><Input id="snapshot-goal-target" type="number" min="0.01" step={draft.goalType === 'count' ? '1' : 'any'} className="mt-2 bg-white" value={draft.target} onChange={(event) => setDraft((current) => ({ ...current, target: event.target.value }))} /></div>
              <div><Label htmlFor="snapshot-goal-unit">Unit</Label><Input id="snapshot-goal-unit" className="mt-2 bg-white" placeholder={draft.goalType === 'count' ? 'conversations' : 'miles'} value={draft.unit} onChange={(event) => updateUnit(event.target.value)} /></div>
              {draft.goalType === 'count' ? <div className="sm:col-span-2"><Label htmlFor="snapshot-count-completion-label">What one completion is called</Label><Input id="snapshot-count-completion-label" className="mt-2 bg-white" maxLength={80} placeholder="conversation" value={draft.completionLabel} onChange={(event) => setDraft((current) => ({ ...current, completionLabel: event.target.value }))} /><p className="mt-1 text-xs leading-5 text-muted-foreground">Used for the quick action: “Mark 1 {draft.completionLabel.trim() || 'completion'} complete.”</p>{fieldErrors.completion_label ? <p className="mt-1 text-xs text-red-700">{fieldErrors.completion_label}</p> : null}</div> : null}
            </div>
          ) : null}

          {draft.goalType === 'milestone' || draft.goalType === 'deadline' ? (
            <div className="space-y-4">
              {draft.goalType === 'deadline' ? <div><Label htmlFor="snapshot-target-date">Target date</Label><Input id="snapshot-target-date" type="date" className="mt-2 bg-white" value={draft.targetDate} onChange={(event) => setDraft((current) => ({ ...current, targetDate: event.target.value }))} /></div> : null}
              <fieldset>
                <legend className="text-sm font-medium">{draft.goalType === 'deadline' ? 'Steps' : 'Milestones'}</legend>
                <div className="mt-2 space-y-2">
                  {draft.steps.map((step, index) => (
                    <div key={step.key ?? `new-${index}`} className="flex items-center gap-1">
                      <Input aria-label={`${draft.goalType === 'deadline' ? 'Step' : 'Milestone'} ${index + 1}`} className="bg-white" maxLength={200} value={step.label} onChange={(event) => updateStep(index, event.target.value)} />
                      <button type="button" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full outline-none hover:bg-white focus-visible:ring-2" disabled={index === 0} onClick={() => moveStep(index, -1)} aria-label={`Move item ${index + 1} up`}><ChevronUp className="h-4 w-4" /></button>
                      <button type="button" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full outline-none hover:bg-white focus-visible:ring-2" disabled={index === draft.steps.length - 1} onClick={() => moveStep(index, 1)} aria-label={`Move item ${index + 1} down`}><ChevronDown className="h-4 w-4" /></button>
                      <button type="button" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-red-700 outline-none hover:bg-red-50 focus-visible:ring-2" disabled={draft.steps.length === 1} onClick={() => setDraft((current) => ({ ...current, steps: current.steps.filter((_, stepIndex) => stepIndex !== index) }))} aria-label={`Remove item ${index + 1}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="ghost" size="auto" className="mt-2 min-h-11" onClick={() => setDraft((current) => ({ ...current, steps: [...current.steps, { label: '' }] }))}><Plus className="mr-1 h-4 w-4" /> Add item</Button>
              </fieldset>
            </div>
          ) : null}

          {draft.goalType === 'reflective' ? (
            <div className="space-y-4">
              <div><Label htmlFor="snapshot-reflection-prompt">Reflection prompt</Label><Textarea id="snapshot-reflection-prompt" className="mt-2 min-h-24 bg-white" maxLength={300} placeholder="What did you notice?" value={draft.prompt} onChange={(event) => setDraft((current) => ({ ...current, prompt: event.target.value }))} /></div>
              <label className="flex min-h-11 items-center gap-3 text-sm">
                <Checkbox checked={draft.reflectiveHasTarget} onCheckedChange={(checked) => setDraft((current) => ({ ...current, reflectiveHasTarget: checked === true }))} />
                Finish after a set number of reflections
              </label>
              {draft.reflectiveHasTarget ? (
                <div className="max-w-xs">
                  <Label htmlFor="snapshot-reflection-target">Finish after</Label>
                  <div className="mt-2 flex items-center gap-2"><Input id="snapshot-reflection-target" type="number" min="1" step="1" className="bg-white" value={draft.target} onChange={(event) => setDraft((current) => ({ ...current, target: event.target.value }))} /><span className="text-sm text-muted-foreground">reflections</span></div>
                  {fieldErrors.target_reflections ? <p className="mt-1 text-xs text-red-700">{fieldErrors.target_reflections}</p> : null}
                </div>
              ) : <p className="text-xs leading-5 text-muted-foreground">Leave this off for an ongoing reflection practice with no finish line.</p>}
            </div>
          ) : null}
          {fieldErrors.tracking_config || fieldErrors.period_unit || fieldErrors.target_date ? <p className="mt-3 text-xs text-red-700">{fieldErrors.tracking_config || fieldErrors.period_unit || fieldErrors.target_date}</p> : null}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label htmlFor="snapshot-start-date">Start date</Label><Input id="snapshot-start-date" type="date" className="mt-2" value={draft.startDate} onChange={(event) => setDraft((current) => ({ ...current, startDate: event.target.value }))} />{fieldErrors.start_date ? <p className="mt-1 text-xs text-red-700">{fieldErrors.start_date}</p> : null}</div>
          <div><Label htmlFor="snapshot-timezone">Timezone</Label><Input id="snapshot-timezone" className="mt-2" value={draft.timezone} onChange={(event) => setDraft((current) => ({ ...current, timezone: event.target.value }))} />{fieldErrors.timezone ? <p className="mt-1 text-xs text-red-700">{fieldErrors.timezone}</p> : null}</div>
        </div>
        <p className="text-xs leading-5 text-muted-foreground">Active goals appear in your daily check-in beginning the day after you create or resume them. Updating or skipping a goal resolves that day’s prompt.</p>

        {trackingSummary ? (
          <section aria-label="Tracking summary" className="rounded-2xl border border-foundation-calm-blue-100 bg-white/70 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Tracking summary</p>
            <p className="mt-2 text-sm font-semibold text-foundation-base-darkbase-dark-500">{trackingSummary.primary}</p>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">{trackingSummary.secondary}</p>
          </section>
        ) : null}
        {partialWeekWarning ? <p role="alert" className="rounded-xl bg-foundation-orange-50 px-3 py-2 text-xs leading-5 text-foundation-orange-900">{partialWeekWarning}</p> : null}

        {error ? <p role="alert" className="rounded-xl bg-foundation-orange-50 px-3 py-2 text-sm text-foundation-orange-900">{error}</p> : null}
        {restartWarning ? (
          <div role="alertdialog" aria-label="Confirm progress restart" className="rounded-2xl border border-foundation-orange-200 bg-foundation-orange-50 p-4">
            <p className="text-sm font-semibold">Start this tracking plan at zero?</p>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">Your previous progress and plan revisions remain in history.</p>
            <div className="mt-3 flex flex-wrap justify-end gap-2"><Button type="button" variant="ghost" size="auto" className="min-h-11" onClick={() => setRestartWarning(false)}>Keep editing</Button><Button type="button" size="auto" className="min-h-11" disabled={saving} onClick={() => save(true)}>Start fresh and save</Button></div>
          </div>
        ) : (
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="ghost" size="auto" className="min-h-11" disabled={saving} onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="button" size="auto" className="min-h-11" disabled={saving || (!dirty && !initialInput) || !draft.title.trim()} onClick={() => save(false)}>{saving ? 'Saving…' : initialInput ? 'Confirm and start tracking' : 'Save goal'}</Button>
          </div>
        )}
      </div>
    </SnapshotEditorShell>
  );
};
